import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Typography,
} from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext, SubscriptionTab } from "contexts/Subscription";
import { useContext } from "react";
import { BannerCouponName, SubscriptionType } from "schemas/dashboard";
import { Plans } from "schemas/payments";

import { useCheckout } from "components/StripeCheckout/useCheckout";
import { useSubscription } from "utils/useSubscription";

import PlansPrice from "../SharedComponents/Price";
import VATerms from "../SharedComponents/VATerms";
import {
  PlanAction,
  getCurrentPlanButton,
  getPlanLimitText,
  isCurrentPlan,
} from "../helpers";
import FollowUpAssistantItem from "./FollowUpAssistantItem";
import styles from "./styles";

interface Props {
  plan: Plans;
}

const VirtualAssistantPlanCard = ({ plan }: Props) => {
  const { currentOrg } = useContext(OrganizationUserContext);
  const { getPromoCodeForPlan } = useCheckout();
  const {
    plans,
    emailSents,
    subscription,
    tab,
    bannerPromoCodes,
    isAtEmailCreditCap,
  } = useContext(SubscriptionContext);
  const { onPlanButtonClick } = useSubscription();

  const promoCode = getPromoCodeForPlan(plan);
  const isMoneyBackGuarantee =
    promoCode?.couponName === BannerCouponName.money_back_guarantee;
  const planName = plans?.[plan]?.display_name;
  const action = getCurrentPlanButton(
    plan,
    subscription?.subscription?.planName || Plans.FREE,
    subscription,
  );

  const BENEFITS = [
    {
      icon: "🚀",
      primary: (
        <strong>
          300 automated emails on your behalf + 700 self serve emails
        </strong>
      ),
    },
    {
      icon: "💸",
      primary: (
        <>
          <strong>Exclusive Brand Pay Data </strong>
        </>
      ),
    },
    {
      icon: "🔑",
      primary: (
        <>
          Get access to <strong>600k+ Brand Contacts</strong>
        </>
      ),
    },
  ];

  const _isCurrentPlan =
    isCurrentPlan(subscription, plan) &&
    (subscription?.subscription?.status === SubscriptionType.ACTIVE ||
      subscription?.subscription?.status === SubscriptionType.PAST_DUE);

  return (
    <>
      <Box sx={styles.card}>
        <Box sx={[styles.planTitleCard, { backgroundColor: "green.dark" }]}>
          <Grid container justifyContent="flex-end">
            <Chip
              sx={{ backgroundColor: "purple.main", px: 1 }}
              label={
                <Typography variant="subtitle2" sx={{ fontSize: 13 }}>
                  <strong>
                    {isMoneyBackGuarantee
                      ? "Money-back guaranteed 💸"
                      : "Hands-off pitching for busy creators"}
                  </strong>
                </Typography>
              }
            ></Chip>
          </Grid>

          <Typography variant="h3" sx={{ mt: 6, color: "white" }}>
            {planName}{" "}
            {_isCurrentPlan && (
              <Box component="span" sx={{ color: "purple.main", fontSize: 16 }}>
                (Current Plan)
              </Box>
            )}
          </Typography>
          <Box sx={styles.price}>
            {tab === SubscriptionTab.monthly && <PlansPrice plan={Plans.VA} />}
            {tab === SubscriptionTab.yearly && (
              <PlansPrice plan={Plans.VA_YEARLY} />
            )}
          </Box>

          <Grid item xs={12}>
            <VATerms plan={plan} />
          </Grid>
          {_isCurrentPlan && !isAtEmailCreditCap && (
            <Box sx={styles.rolloverBox}>
              <Box sx={{ ...styles.rolloverRow, mb: 0.5 }}>
                <Typography sx={styles.rolloverLabelDim}>
                  Email Credits Used
                </Typography>
                <Typography sx={styles.rolloverValue}>
                  {emailSents} /{" "}
                  {Math.max(emailSents, subscription?.monthlyLimit || 0)}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(
                  100,
                  (emailSents /
                    Math.max(1, subscription?.monthlyLimit || emailSents)) *
                    100,
                )}
                sx={styles.rolloverProgress}
              />
              <Box sx={{ ...styles.rolloverRow, mb: 0.25 }}>
                <Typography sx={styles.rolloverLabel}>
                  Monthly Allotment
                </Typography>
                <Typography sx={styles.rolloverLabel}>
                  {(
                    (subscription?.monthlyLimit || 0) -
                    (subscription?.rolloverEmailCredits || 0)
                  ).toLocaleString()}
                </Typography>
              </Box>
              {(subscription?.rolloverEmailCredits || 0) > 0 && (
                <Box sx={styles.rolloverRow}>
                  <Typography sx={styles.rolloverLabel}>
                    Rolled over from last month
                  </Typography>
                  <Typography sx={styles.rolloverValueAccent}>
                    +{subscription!.rolloverEmailCredits!.toLocaleString()}
                  </Typography>
                </Box>
              )}
              <Divider sx={styles.rolloverDivider} />
              <Box sx={styles.rolloverRow}>
                <Typography sx={styles.rolloverTotal}>
                  Total available this month
                </Typography>
                <Typography sx={styles.rolloverTotal}>
                  {Math.max(
                    emailSents,
                    subscription?.monthlyLimit || 0,
                  ).toLocaleString()}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {!isAtEmailCreditCap && (
          <Box sx={styles.rolloverInfoBox}>
            <Box sx={styles.rolloverInfoHeader}>
              <Box
                component="i"
                className="fa-solid fa-arrow-trend-up"
                sx={styles.rolloverInfoIcon}
              />
              <Typography sx={styles.rolloverInfoTitle}>
                {_isCurrentPlan ? "Credit Rollover Active" : "Rollover Credits"}
              </Typography>
            </Box>
            <Typography sx={styles.rolloverInfoDesc}>
              {_isCurrentPlan
                ? "Unused credits roll over each month (up to 250/month). You can build up a maximum of 1,500 total email credits per month."
                : "Unused emails roll over monthly"}
            </Typography>
          </Box>
        )}

        <Typography sx={{ mb: 2 }}>
          <strong>Everything in Pro Plan PLUS:</strong>
        </Typography>

        {BENEFITS.map((benefit, index) => (
          <Grid container key={index} alignItems="center" sx={{ mb: 1.5 }}>
            <Grid
              container
              justifyContent="left"
              alignItems="center"
              item
              xs="auto"
            >
              <Box sx={styles.iconBlock}>{benefit.icon}</Box>
            </Grid>
            <Grid item xs md>
              <Typography component="div">{benefit.primary}</Typography>
            </Grid>
          </Grid>
        ))}
        <FollowUpAssistantItem
          isCurrentPlan={_isCurrentPlan}
          subscription={subscription}
        />
        {!currentOrg?.overridePlanName && (
          <Button
            disableElevation
            fullWidth
            variant={action === PlanAction.upgrade ? "contained" : "text"}
            sx={{
              mt: "auto",
              py: 1,
              borderRadius: 2,
              backgroundColor:
                action === PlanAction.upgrade ? "primary.main" : "#f4f8f6",
            }}
            onClick={() => onPlanButtonClick(plan, action)}
          >
            {action}
          </Button>
        )}
      </Box>

      <Box sx={{ mt: 2 }}>
        {getPlanLimitText(
          subscription,
          plan,
          emailSents,
          plans,
          bannerPromoCodes,
        )}
      </Box>
    </>
  );
};

export default VirtualAssistantPlanCard;
